import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { Product, ProductService } from '../api/api_pb';
import { useClient } from '../connect_api';
import { toast } from 'react-toastify';

interface ProductOption {
  value: string;
  label: string;
  product: Product;
}

interface ProductMultiSelectProps {
  selectedProducts: string[];
  onSelectionChange: (productUuids: string[]) => void;
  disabled?: boolean;
}

const ProductMultiSelect: React.FC<ProductMultiSelectProps> = ({
  selectedProducts,
  onSelectionChange,
  disabled = false
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const productClient = useClient(ProductService);

  useEffect(() => {
    let isMounted = true;
    
    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await productClient.listProducts({});
        if (isMounted) {
          setProducts(response.products);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load products');
          console.error('Error fetching products:', err);
          toast.error('Failed to load products');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchProducts();
    
    return () => {
      isMounted = false;
    };
  }, [productClient]);

  const productOptions: ProductOption[] = products.map(product => ({
    value: product.uuid,
    label: product.name,
    product
  }));

  const selectedOptions = productOptions.filter(option => 
    selectedProducts.includes(option.value)
  );

  const handleChange = (selectedOptions: readonly ProductOption[] | null) => {
    const productUuids = selectedOptions ? selectedOptions.map(option => option.value) : [];
    onSelectionChange(productUuids);
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '42px',
      borderColor: state.isFocused ? '#0d6efd' : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#0d6efd' : '#adb5bd'
      }
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#6c757d',
      fontStyle: 'italic'
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: '200px'
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#495057',
      fontSize: '14px',
      padding: '8px 12px',
      cursor: 'pointer'
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#fff3cd',
      border: '1px solid #ffeaa7'
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#856404',
      fontSize: '13px'
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#6c757d',
      ':hover': {
        backgroundColor: '#dc3545',
        color: 'white',
      },
    }),
  };

  return (
    <Form.Group>
      <Form.Label className="text-sm font-medium text-gray-700">
        Filter by Products
      </Form.Label>
      <Select
        isMulti
        isSearchable={true}
        options={productOptions}
        value={selectedOptions}
        onChange={handleChange}
        placeholder="Type to search and select products..."
        isLoading={isLoading}
        isDisabled={disabled || isLoading}
        styles={customStyles}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        noOptionsMessage={({ inputValue }) => 
          error ? error : inputValue ? `No products found matching "${inputValue}"` : 'No products available'
        }
        loadingMessage={() => 'Loading products...'}
        className="react-select-container"
        classNamePrefix="react-select"
        filterOption={(option, inputValue) => {
          const product = option.data.product;
          const searchTerm = inputValue.toLowerCase();
          return Boolean(product.name.toLowerCase().includes(searchTerm));
        }}
        menuIsOpen={undefined}
        menuShouldBlockScroll={false}
      />
      {error && (
        <Form.Text className="text-danger">
          {error}
        </Form.Text>
      )}
      {!error && (
        <Form.Text className="text-muted">
          Type to search by product name
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default ProductMultiSelect;